/**
 * Central registry of upsell gate copy — feature name + description for each Pro gate.
 *
 * ALL UpsellSheet instances must import copy from here. No inline string literals
 * in components (RULE-056 prevention).
 */

export interface UpsellGateCopy {
  /** Short feature name shown in DM Serif Display — e.g. "Hunt Journal" */
  featureName: string;
  /** 2-sentence value proposition shown as body text */
  description: string;
}

export type UpsellGateKey =
  | 'second_property'
  | 'template_library'
  | 'shopping_list_limit'
  | 'scouting_environmental'
  | 'log_entry_photos'
  | 'yoy_comparison'
  | 'pattern_surfacing'
  | 'hunt_journal'
  | 'cell_cameras'
  | 'public_land_overlay'
  | 'eight_day_forecast'
  | 'cloud_sync';

export const UPSELL_COPY: Record<UpsellGateKey, UpsellGateCopy> = {
  second_property: {
    featureName: 'Multiple Properties',
    description:
      'Manage multiple properties with Pro. Track stands, plots, and cameras across all your land.',
  },
  template_library: {
    featureName: 'Full Template Library',
    description:
      'Access all prep templates including Bow Season, Food Plot, and Lease Hunter with Pro.',
  },
  shopping_list_limit: {
    featureName: 'Unlimited Shopping List',
    description:
      'Unlimited shopping list items with Pro. Never lose track of your gear list.',
  },
  scouting_environmental: {
    featureName: 'Environmental Auto-Fill',
    description:
      'Auto-populate weather and moon data, attach trail cam photos, and compare seasons with Pro.',
  },
  log_entry_photos: {
    featureName: 'Scouting Photos',
    description:
      'Attach trail cam photos and images to your scouting log entries with Pro.',
  },
  yoy_comparison: {
    featureName: 'Year-Over-Year Comparison',
    description:
      'Compare scouting data across seasons to find patterns on your land with Pro.',
  },
  pattern_surfacing: {
    featureName: 'Pattern Analysis',
    description:
      'Discover when and where deer move on your land with Pro pattern analysis.',
  },
  hunt_journal: {
    featureName: 'Hunt Journal',
    description:
      'Log your hunts, sits, and harvests. Capture the story of every season with Pro.',
  },
  cell_cameras: {
    featureName: 'Cell Camera Integration',
    description:
      'Connect your Spypoint cameras and view photos directly in Ridgen with Pro.',
  },
  public_land_overlay: {
    featureName: 'Public Land Overlay',
    description:
      'See public land boundaries on your map — state forests, WMAs, and national forests with Pro.',
  },
  eight_day_forecast: {
    featureName: '8-Day Forecast',
    description:
      'Plan your hunts with an 8-day hunter-focused forecast including quality scores with Pro.',
  },
  cloud_sync: {
    featureName: 'Cloud Sync',
    description:
      'Sync your data across all your devices — phone, tablet, and desktop with Pro.',
  },
};
